import Avatar from '@mui/material/Avatar';
import type { Currency } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import flags from '@swf/core/assets/flags';

import ExchangeIcon from '../../../../../../../../assets/exchange.svg';
import { convertAmountByRate, isZeroExponentCurrency } from '../../../../../../../../utils/cashier';
import ExchangeRateFormula from '../../../../../../../ExchangeRateFormula/ExchangeRateFormula';

import {
  avatar,
  container,
  delimeter,
  exchangeRate,
  main,
  record,
  sign
} from './AmountSummaryRecord.scss';

interface Props {
  amount: number;
  className?: string;
  currency: Currency;
  fee?: number;
  isSingleRecord?: boolean;
  isPayeeRecordShown?: boolean;
  label: TranslationTag;
  rate?: number;
  selectedCurrency?: Currency;
  variant: 'pay' | 'commission' | 'conversion' | 'total' | 'rate';
}
const AmountSummaryRecord: React.FC<Props> = ({
  amount,
  className,
  currency,
  fee = 0,
  isSingleRecord,
  isPayeeRecordShown,
  label,
  rate,
  selectedCurrency,
  variant
}) => {
  const isMainStyle = ['total', 'pay'].includes(variant);
  const isExchangeRate = variant === 'rate';

  return (
    <div
      className={classNames(
        container,
        isMainStyle && main,
        !isSingleRecord && !isPayeeRecordShown && isMainStyle && delimeter,
        className
      )}
    >
      <div>
        <Translation {...label} />
      </div>
      <div
        className={classNames(record, { [exchangeRate]: isExchangeRate })}
        data-testid={`${variant}value`}
      >
        {amount && currency && !isExchangeRate && (
          <>
            <div className={sign}>
              {isMainStyle && '+'}
              {variant === 'commission' && '-'}
            </div>
            <CurrencyAmount
              value={
                rate ? Number(convertAmountByRate(amount, rate, currency, false) - fee) : amount
              }
              isZeroExponentCurrency={isZeroExponentCurrency(currency)}
              currency={currency}
              currencyDisplay="code"
              dollarValue
            />
            <div>
              {isMainStyle && (
                <Avatar classes={{ root: avatar }} src={flags[currency.substring(0, 2)]} />
              )}
              {variant === 'conversion' && <ExchangeIcon />}
            </div>
          </>
        )}
        {isExchangeRate && selectedCurrency && rate && (
          <ExchangeRateFormula
            maximumFractionDigits={4}
            minimumFractionDigits={4}
            sourceAmountInCents={100}
            sourceCurrency={selectedCurrency}
            targetCurrency={currency}
            rate={rate}
            inverted={false}
          />
        )}
      </div>
    </div>
  );
};

export default AmountSummaryRecord;
